import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { useMe } from '../App.jsx';
import EmailThreads from '../EmailThreads.jsx';
import MediaGallery from '../MediaGallery.jsx';
import { BoxIcon, CameraIcon, JobPage, NoteIcon, PencilIcon, PersonIcon, Spinner } from '../components.jsx';

function Action({ to, icon: Icon, title, sub }) {
  return (
    <Link to={to} className="card flex items-center gap-4 p-5 active:scale-[0.99] transition">
      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-navy text-white dark:bg-steel">
        <Icon className="h-8 w-8" />
      </span>
      <span>
        <span className="block text-xl font-bold">{title}</span>
        <span className="block text-steel">{sub}</span>
      </span>
    </Link>
  );
}

// Best-effort: try to open the folder in the NextCloud mobile app instead of its web UI.
// It works some of the time, so "Open folder" always sits next to it.
// Android: a Chrome "intent:" link aimed at the app's package; if the app can't take it,
// Chrome opens the normal web folder instead. iOS: the app's own nextcloud://open-file link
// (needs the person's NextCloud username; falls back to nothing if the app isn't installed).
function appLink(job, folder, user) {
  if (!folder) return null;
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) {
    const base = new URL(folder.ncBase);
    const path = `${base.pathname.replace(/\/$/, '')}/index.php/f/${folder.fileId}`;
    return `intent://${base.host}${path}#Intent;scheme=${base.protocol.replace(':', '')};package=com.nextcloud.client;S.browser_fallback_url=${encodeURIComponent(job.folderUrl || folder.ncBase)};end`;
  }
  if (/iPhone|iPad|iPod/i.test(ua) && user.username) {
    const link = `${folder.ncBase}/index.php/f/${folder.fileId}`;
    return `nextcloud://open-file?path=${encodeURIComponent(folder.path)}&user=${encodeURIComponent(user.username)}&link=${encodeURIComponent(link)}`;
  }
  return null;
}

// Staff level only: what's already been captured for this job.
function Activity({ job }) {
  const { user } = useMe();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/api/jobs/${job.id}/activity`).then(setData).catch(() => setData({ stream: null, media: null }));
  }, [job.id]);

  return (
    <section className="space-y-3">
      <h2 className="label !mb-0">Recent activity</h2>
      {!data && <div className="flex justify-center py-4"><Spinner /></div>}
      {data?.media && (
        <p className="text-sm">
          <b>{data.media.photos}</b> photo{data.media.photos === 1 ? '' : 's'}{data.media.sketches ? <>, <b>{data.media.sketches}</b> sketch{data.media.sketches === 1 ? '' : 'es'}</> : null} and <b>{data.media.videos}</b> video{data.media.videos === 1 ? '' : 's'} in NextCloud
        </p>
      )}
      {data?.stream?.length === 0 && <p className="text-sm text-steel">No notes yet.</p>}
      {data?.stream?.map((n) => (
        <div key={n.id} className="card p-3 text-sm">
          <div className="mb-1 text-xs text-steel">{n.by} · {new Date(n.at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}</div>
          <div className="line-clamp-5 whitespace-pre-wrap break-words">{n.text}</div>
        </div>
      ))}
      {data && data.stream === null && <p className="text-sm text-steel">Couldn't load the CRM activity just now.</p>}
      <div className="flex flex-wrap gap-3 pt-1">
        {job.crmUrl && <a className="btn-ghost !py-2.5 !text-base" href={job.crmUrl} target="_blank" rel="noreferrer">Open in CRM</a>}
        {job.folderUrl && <a className="btn-ghost !py-2.5 !text-base" href={job.folderUrl} target="_blank" rel="noreferrer">Open folder</a>}
        {appLink(job, data?.folder, user) && (
          <a className="btn-ghost !py-2.5 !text-base" href={appLink(job, data.folder, user)}>Open in NextCloud app</a>
        )}
      </div>
    </section>
  );
}

export default function Job() {
  const { user } = useMe();
  return (
    <JobPage backTo="search">
      {(job) => (
        <>
          <div className="card p-5">
            {job.jobNumber && <span className="badge mb-2 bg-amber text-charcoal">{job.jobNumber}</span>}
            <h1 className="text-2xl font-bold leading-snug">{job.name}</h1>
            <p className="mt-1 text-steel">{[job.account, job.siteAddress].filter(Boolean).join(' · ')}</p>
          </div>
          {user.role === 'warehouse' ? (
            <div className="space-y-3">
              <Action to={`/job/${job.id}/goods/out`} icon={BoxIcon} title="Sent to job" sub="Photos and what went out" />
              <Action to={`/job/${job.id}/goods/in`} icon={BoxIcon} title="Returned from job" sub="Photos and what came back" />
            </div>
          ) : (
          <div className="space-y-3">
            <Action to={`/job/${job.id}/note`} icon={NoteIcon} title="Add a note" sub="Type what happened" />
            <Action to={`/job/${job.id}/files`} icon={CameraIcon} title="Add photos / videos" sub="Take new or choose from your phone" />
            <Action to={`/job/${job.id}/sketch`} icon={PencilIcon} title="Add a sketch" sub="Draw a room plan with measurements" />
            <Action to={`/job/${job.id}/contact`} icon={PersonIcon} title="Add a contact" sub="Name and phone number" />
          </div>
          )}
          {user.role === 'staff' && (
            <>
              <Activity job={job} />
              <MediaGallery job={job} />
              <EmailThreads job={job} />
            </>
          )}
        </>
      )}
    </JobPage>
  );
}
